import type { AnalyticsConfig } from './analyticsConfig';
import { getName, getTitle } from '@/components/Constants';
import type {
  AssociationRow,
  MetricVsScorePoint,
  ScoreDistributionSlice,
  TopEntityBarItem,
  YearPublicationPoint,
} from './analyticsTypes';

export const CT_COLOR_BUCKETS = [
  { max: 0, color: '#1a4d7a', label: '0 clinical trials' },
  { max: 20, color: '#4a9fd4', label: '1–20 clinical trials' },
  { max: 100, color: '#f5a623', label: '21–100 clinical trials' },
  { max: Infinity, color: '#e67e22', label: '>100 clinical trials' },
] as const;

const SCORE_DISTRIBUTION_COLORS = [
  '#d4e4f7',
  '#a8c9eb',
  '#7caddf',
  '#5092d3',
  '#3a7ab8',
  '#2d6299',
  '#1f4a7a',
  '#14365d',
  '#0d2847',
  '#081c33',
];

const DEFAULT_BAR_COLOR = '#3b82c4';

export function getCtColor(numCTs: number): string {
  const value = Number(numCTs) || 0;
  if (value === 0) return CT_COLOR_BUCKETS[0].color;
  if (value <= 20) return CT_COLOR_BUCKETS[1].color;
  if (value <= 100) return CT_COLOR_BUCKETS[2].color;
  return CT_COLOR_BUCKETS[3].color;
}

export function getRowFieldValue(row: AssociationRow, field: string): string | number | null | undefined {
  const value = row[field];
  if (Array.isArray(value)) return value[0] as string | number | null | undefined;
  return value;
}

export function average(values: number[]): number {
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function getAssociatedCount(data: AssociationRow[], field: string, entityIdField?: string): number {
  if (!data.length) return 0;

  if (entityIdField) {
    const uniqueEntities = new Set(
      data.map(row => String(getRowFieldValue(row, entityIdField) ?? '').trim()).filter(Boolean),
    );
    if (uniqueEntities.size > 0) {
      const aggregateValue = Number(getRowFieldValue(data[0], field));
      const shouldUseDistinct =
        data.length > 1 ||
        !Number.isFinite(aggregateValue) ||
        aggregateValue <= 0 ||
        aggregateValue > uniqueEntities.size;
      if (shouldUseDistinct) return uniqueEntities.size;
    }
  }

  const fromRow = Number(getRowFieldValue(data[0], field));
  if (Number.isFinite(fromRow) && fromRow > 0) return fromRow;
  return data.length;
}

type SummaryMatchCardContext = {
  stats: { matchFilterCount: number; matchFilterLabel: string } | null;
  resultTotalCount: number | null;
};

function matchesSummaryMatchLabel(cardLabel: string, matchFilterLabel: string): boolean {
  const normalizedCard = cardLabel.toLowerCase();
  const normalizedMatch = matchFilterLabel.toLowerCase();
  if (normalizedMatch.includes('gene')) return normalizedCard.includes('gene');
  if (normalizedMatch.includes('disease')) return normalizedCard.includes('disease');
  if (normalizedMatch.includes('variant')) return normalizedCard.includes('variant');
  return false;
}

export function getSummaryCardValue(
  card: { id: string; getValue: (data: AssociationRow[], config: AnalyticsConfig) => number },
  data: AssociationRow[],
  config: AnalyticsConfig,
  summaryMatch?: SummaryMatchCardContext | null,
): number {
  if (summaryMatch?.stats && card.id === 'associated') {
    const cardLabel =
      typeof config.labels.associatedCount === 'string' ? config.labels.associatedCount : 'associated';
    if (matchesSummaryMatchLabel(cardLabel, summaryMatch.stats.matchFilterLabel)) {
      return summaryMatch.stats.matchFilterCount;
    }
    if (summaryMatch.resultTotalCount != null) {
      return summaryMatch.resultTotalCount;
    }
  }

  return card.getValue(data, config);
}

export function getActivePmidsCurrentYear(
  data: AssociationRow[],
  pmidsField: string,
  yearField?: string,
): number {
  if (!yearField) return 0;
  const currentYear = new Date().getFullYear();
  return data
    .filter(row => Number(row[yearField]) >= currentYear)
    .reduce((sum, row) => sum + (Number(row[pmidsField]) || 0), 0);
}

function mapTopEntities(
  data: AssociationRow[],
  sortField: string,
  config: AnalyticsConfig,
  limit: number,
): TopEntityBarItem[] {
  return [...data]
    .sort(
      (a, b) => (Number(getRowFieldValue(b, sortField)) || 0) - (Number(getRowFieldValue(a, sortField)) || 0),
    )
    .slice(0, limit)
    .map(row => {
      const numCTs = config.fields.ct ? Number(getRowFieldValue(row, config.fields.ct)) || 0 : 0;
      const entityId = String(getRowFieldValue(row, config.fields.entityId) ?? '');
      return {
        entityId,
        normalizedScore: Number(getRowFieldValue(row, config.fields.score)) || 0,
        numPMIDs: Number(getRowFieldValue(row, config.fields.papers)) || 0,
        numCTs,
        color: config.features.ctLegend ? getCtColor(numCTs) : DEFAULT_BAR_COLOR,
      };
    });
}

export function getTopEntitiesByScore(
  data: AssociationRow[],
  config: AnalyticsConfig,
  limit = 15,
): TopEntityBarItem[] {
  return mapTopEntities(data, config.fields.score, config, limit);
}

export function getTopEntitiesByPapers(
  data: AssociationRow[],
  config: AnalyticsConfig,
  limit = 15,
): TopEntityBarItem[] {
  return mapTopEntities(data, config.fields.papers, config, limit);
}

export function getCumulativePublicationsByYear(
  data: AssociationRow[],
  yearField: string,
  pmidsField: string,
): YearPublicationPoint[] {
  const yearCounts = new Map<number, number>();

  data.forEach(row => {
    const year = Number(row[yearField]);
    const pmids = Number(row[pmidsField]) || 0;
    if (!Number.isFinite(year) || pmids <= 0) return;
    yearCounts.set(year, (yearCounts.get(year) || 0) + pmids);
  });

  const years = [...yearCounts.keys()].sort((a, b) => a - b);
  if (!years.length) return [];

  const minYear = Math.min(...years);
  const maxYear = Math.max(...years);
  let cumulative = 0;
  const points: YearPublicationPoint[] = [];

  for (let year = minYear; year <= maxYear; year += 1) {
    const count = yearCounts.get(year) || 0;
    cumulative += count;
    points.push({ year, count, cumulative });
  }

  return points;
}

export function getScoreDistribution(data: AssociationRow[], scoreField: string): ScoreDistributionSlice[] {
  const bucketSize = 0.1;
  const buckets = Array.from({ length: 10 }, (_, index) => ({
    min: index * bucketSize,
    max: (index + 1) * bucketSize,
    count: 0,
  }));

  data.forEach(row => {
    const score = Number(row[scoreField]);
    if (!Number.isFinite(score)) return;
    const clamped = Math.min(Math.max(score, 0), 0.999999);
    const bucketIndex = Math.min(Math.floor(clamped / bucketSize), buckets.length - 1);
    buckets[bucketIndex].count += 1;
  });

  const total = data.length || 1;

  return buckets.map((bucket, index) => {
    const minLabel = bucket.min.toFixed(1);
    const maxLabel = bucket.max.toFixed(1);
    const percentage = Math.round((bucket.count / total) * 100);
    const isLastBucket = index === buckets.length - 1;

    return {
      id: `${minLabel}-${maxLabel}`,
      label: `${minLabel}–${maxLabel} (${percentage}%)`,
      value: bucket.count,
      color: SCORE_DISTRIBUTION_COLORS[index] ?? SCORE_DISTRIBUTION_COLORS.at(-1)!,
      minScore: bucket.min,
      maxScore: bucket.max,
      isLastBucket,
    };
  });
}

export function getMetricVsScoreData(
  data: AssociationRow[],
  config: AnalyticsConfig,
  metricKey: string,
  limit = 50,
): MetricVsScorePoint[] {
  return [...data]
    .sort(
      (a, b) =>
        (Number(getRowFieldValue(b, config.fields.score)) || 0) -
        (Number(getRowFieldValue(a, config.fields.score)) || 0),
    )
    .slice(0, limit)
    .map(row => ({
      id: String(getRowFieldValue(row, config.fields.entityId) ?? ''),
      entityId: String(getRowFieldValue(row, config.fields.entityId) ?? ''),
      x: Number(getRowFieldValue(row, metricKey)) || 0,
      y: Number(getRowFieldValue(row, config.fields.score)) || 0,
    }));
}

export function pickRandomScatterLabelIds(entityIds: string[], count = 10): Set<string> {
  const uniqueIds = [...new Set(entityIds.filter(Boolean))];
  if (uniqueIds.length <= count) return new Set(uniqueIds);

  const shuffled = [...uniqueIds];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
  }

  return new Set(shuffled.slice(0, count));
}

const LABEL_ELLIPSIS = '...';

export const BAR_CHART_AXIS_LABEL_MAX_LENGTH = 15;

export function truncateChartLabel(label: string, maxLength = 24): string {
  if (label.length <= maxLength) return label;
  return `${label.slice(0, maxLength - LABEL_ELLIPSIS.length)}${LABEL_ELLIPSIS}`;
}

export function truncateScatterLabel(label: string, maxLength = 24): string {
  return truncateChartLabel(label, maxLength);
}

export function formatMetricNumber(value: number, decimals = 0): string {
  if (!Number.isFinite(value)) return '—';
  if (decimals > 0) return value.toFixed(decimals);
  return Math.round(value).toLocaleString('es-ES');
}

export function buildSummaryValues(data: AssociationRow[], config: AnalyticsConfig): Record<string, number> {
  return Object.fromEntries(config.summaryCards.map(card => [card.id, card.getValue(data, config)]));
}

export function getSummaryCardLabel(
  card: AnalyticsConfig['summaryCards'][number],
  currentYear: number,
): string {
  return typeof card.label === 'function' ? card.label(currentYear) : card.label;
}

function toSearchItemLabel(item: {
  id?: unknown;
  name?: unknown;
  label?: unknown;
  [key: string]: unknown;
}): string {
  const title = getTitle(item);
  const id = getName(item);

  if (typeof title === 'string' && title) return title;
  if (typeof title === 'number' || typeof title === 'boolean') return String(title);
  if (typeof title === 'object' && title !== null && 'title' in title) {
    return String((title as { title?: unknown }).title ?? id ?? item.id ?? 'Item');
  }

  const fallback = id || item.id;
  return fallback != null && fallback !== '' ? String(fallback) : 'Item';
}

export function buildAnalyticsSearchTitle(
  names: Array<{ id?: unknown; name?: unknown; label?: unknown; [key: string]: unknown }>,
): string {
  if (!names?.length) return 'Search';

  if (names.length === 1) return toSearchItemLabel(names[0]);

  const preview = names.slice(0, 3).map(toSearchItemLabel).join(', ');
  return names.length > 3 ? `${preview} +${names.length - 3} more` : preview;
}

export { getTopEntitiesByScore as getTopGenesByScore };
export { getTopEntitiesByPapers as getTopGenesByPapers };
export type { AssociationRow as GdaAssociationRow };
